import type { Expression } from '@zenstackhq/schema';
import { schema } from '../__generated__/schema';

type Op = 'create' | 'read' | 'update' | 'delete';
type Auth = Record<string, unknown> | null | undefined;
type Row = Record<string, unknown> | undefined;

/** Evaluate a single `@@allow(op, condition)` expression in the browser.
 *  Covers the expressions our schema currently uses:
 *    literal, member (e.g. `auth().role`), field (`id`), call('auth'),
 *    unary !, binary == != && || > >= < <=.
 *  Returns `undefined` if the AST contains a node it can't evaluate (be conservative).
 *  In that case, fall back to "assume the server will decide" and just let the user try. */
export function evaluate(expr: Expression, auth: Auth, row: Row): unknown {
  switch (expr.kind) {
    case 'literal':
      return (expr as { value: unknown }).value;
    case 'field': {
      const name = (expr as { field: string }).field;
      return row?.[name];
    }
    case 'call': {
      const e = expr as unknown as { function: string; args?: Expression[] };
      if (e.function === 'auth') return auth ?? null;
      return undefined;
    }
    case 'member': {
      const e = expr as { receiver: Expression; members: string[] };
      let current: unknown = evaluate(e.receiver, auth, row);
      for (const m of e.members) {
        if (current == null || typeof current !== 'object') return undefined;
        current = (current as Record<string, unknown>)[m];
      }
      return current;
    }
    case 'unary': {
      const e = expr as { op: string; operand: Expression };
      const v = evaluate(e.operand, auth, row);
      if (e.op === '!') return !v;
      return undefined;
    }
    case 'binary': {
      const e = expr as { left: Expression; op: string; right: Expression };
      const l = evaluate(e.left, auth, row);
      const r = evaluate(e.right, auth, row);
      switch (e.op) {
        case '==':
          return l === r;
        case '!=':
          return l !== r;
        case '&&':
          return Boolean(l) && Boolean(r);
        case '||':
          return Boolean(l) || Boolean(r);
        case '>':
          return (l as number) > (r as number);
        case '>=':
          return (l as number) >= (r as number);
        case '<':
          return (l as number) < (r as number);
        case '<=':
          return (l as number) <= (r as number);
        default:
          return undefined;
      }
    }
    default:
      return undefined;
  }
}

type ModelName = keyof typeof schema.models;
type AttrApp = { name: string; args?: Array<{ name: string; value: unknown }> };

function findAttrs(attrs: readonly AttrApp[], name: string, op: Op | 'read'): Array<{ condition: Expression }> {
  const out: Array<{ condition: Expression }> = [];
  for (const attr of attrs) {
    if (attr.name !== name) continue;
    const args = attr.args ?? [];
    const operation = args.find((a) => a.name === 'operation')?.value as
      | { kind: string; value: string }
      | undefined;
    const condition = args.find((a) => a.name === 'condition')?.value as Expression | undefined;
    if (!operation || !condition) continue;
    if (operation.value !== op && operation.value !== 'all') continue;
    out.push({ condition });
  }
  return out;
}

/** Returns true if ANY `@@allow(op, condition)` rule for `model` evaluates truthy given `auth` and `row`.
 *  When the schema declares no rule for the op, defaults to `false` (zmodel default-deny). */
export function can(model: ModelName, op: Op, auth: Auth, row?: Row): boolean {
  const m = (
    schema.models as unknown as Record<string, { attributes?: AttrApp[] }>
  )[model as string];
  const allows = findAttrs(m?.attributes ?? [], '@@allow', op);
  if (allows.length === 0) return false;
  return allows.some((r) => Boolean(evaluate(r.condition, auth, row)));
}

/** Field-level policy check. Stacks on top of model-level `can()`: the field is updatable
 *  iff no field `@deny` matches AND (no field `@allow` exists OR at least one matches).
 *  If the field declares no field-level rules, defaults to true — model-level decides. */
export function canField(
  model: ModelName,
  fieldName: string,
  op: 'update' | 'read',
  auth: Auth,
  row?: Row,
): boolean {
  const fields = (
    schema.models as unknown as Record<string, { fields: Record<string, { attributes?: AttrApp[] }> }>
  )[model as string]?.fields;
  const attrs = fields?.[fieldName]?.attributes ?? [];

  const denies = findAttrs(attrs, '@deny', op);
  if (denies.some((r) => Boolean(evaluate(r.condition, auth, row)))) return false;

  const allows = findAttrs(attrs, '@allow', op);
  if (allows.length === 0) return true;
  return allows.some((r) => Boolean(evaluate(r.condition, auth, row)));
}
